import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Landmark, Globe2, FileCheck2, Lock } from "lucide-react";
import { Button } from "../ui/button";
import HeroIllustration from "../illustrations/HeroIllustration";

const TRUST_CHIPS = [
  { icon: ShieldCheck, label: "SEBI-registered advisors" },
  { icon: Landmark, label: "NRE / NRO ready" },
  { icon: FileCheck2, label: "DTAA tax support" },
  { icon: Lock, label: "AES-256 encrypted" },
];

const STATS = [
  { label: "NRIs served", value: "2,400+" },
  { label: "Countries covered", value: "32" },
  { label: "Assets advised", value: "₹680 Cr" },
  { label: "Avg. onboarding", value: "5 days" },
];

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-28 lg:pt-32 pb-16 lg:pb-20 overflow-hidden"
      style={{ background: "transparent" }}
    >
      {/* Indian-inspired ambient accents — Ashoka Blue, Saffron, Peacock Green */}
      <div
        aria-hidden
        className="absolute -top-52 right-[-12%] w-[620px] h-[620px] rounded-full opacity-[0.22] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-gold)) 0%, transparent 65%)" }}
      />
      <div
        aria-hidden
        className="absolute top-[40%] left-[-18%] w-[520px] h-[520px] rounded-full opacity-[0.15] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-sage)) 0%, transparent 65%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-[-10%] right-[-5%] w-[400px] h-[400px] rounded-full opacity-[0.08] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-teal)) 0%, transparent 65%)" }}
      />
      <div aria-hidden className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      {/* Saffron top border accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[hsl(var(--zt-gold))] to-transparent opacity-60" />

      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center relative z-10">
        {/* ── Left: copy ─────────────────────────────────────────── */}
        <motion.div
          className="lg:col-span-6 space-y-7"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Status pill */}
          <div className="inline-flex items-center gap-2.5 rounded-full bg-white border border-[hsl(var(--zt-beige-deep))] pl-2.5 pr-4 py-1.5 elev-1">
            <span className="relative flex h-1.5 w-1.5 text-[hsl(var(--zt-sage))]">
              <span className="pulse-dot absolute inset-0 rounded-full bg-current" />
              <span className="relative rounded-full h-1.5 w-1.5 bg-current" />
            </span>
            <span className="font-heading text-[11px] font-semibold tracking-[0.1em] uppercase text-[hsl(var(--zt-charcoal))]">
              Now onboarding from 32 countries
            </span>
          </div>

          <h1 className="font-display text-[2.6rem] sm:text-[3.4rem] lg:text-[4.15rem] leading-[1.04] text-[hsl(var(--zt-teal-deep))]">
            Invest FCNR funds
            <br />
            in{" "}
            <span className="relative inline-block text-[hsl(var(--zt-gold))]">
              India's growth
              <motion.span
                aria-hidden
                className="absolute left-0 -bottom-1 h-[3px] rounded-full bg-gradient-to-r from-[hsl(var(--zt-gold))] to-[hsl(var(--zt-sage))] opacity-50"
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
              />
            </span>
            {"."}
          </h1>

          {/* Highlighted returns badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="relative inline-flex items-center gap-3 rounded-2xl bg-gradient-to-r from-[hsl(var(--zt-teal-deep))] to-[hsl(var(--zt-teal))] px-6 py-4 shadow-lg"
          >
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--zt-gold))] opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[hsl(var(--zt-gold))]" />
            </span>
            <span className="font-display text-[1.3rem] sm:text-[1.6rem] font-bold text-white tracking-tight">
              Earn up to <span className="text-[hsl(var(--zt-gold))] text-[1.5rem] sm:text-[1.9rem]">20% returns</span> on NRI investments
            </span>
          </motion.div>

          <p className="text-[1.0625rem] lg:text-lg text-[hsl(var(--zt-charcoal))]/85 leading-[1.7] max-w-xl font-body">
            Convert your foreign currency earnings into FCNR accounts and grow your wealth in India's thriving markets. 
            Full FEMA compliance, tax free investments, and SEBI-registered advisors — all in one platform built for overseas Indians.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-3 pt-1">
            <Button
              data-testid="hero-primary-cta"
              onClick={() => document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" })}
              className="group bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 rounded-xl px-7 h-12 font-heading text-sm font-semibold tracking-tight elev-2 transition-all duration-200 hover:-translate-y-0.5"
            >
              Start FCNR Investment
              <ArrowRight className="ml-2 transition-transform duration-200 group-hover:translate-x-0.5" size={17} />
            </Button>
            <Button
              data-testid="hero-secondary-cta"
              variant="ghost"
              onClick={() => document.getElementById("fcnr")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-white border border-[hsl(var(--zt-beige-deep))] text-[hsl(var(--zt-teal))] hover:bg-white hover:text-[hsl(var(--zt-teal))] hover:border-[hsl(var(--zt-gold))]/40 rounded-xl px-7 h-12 font-heading text-sm font-semibold tracking-tight elev-1 transition-all duration-200 hover:-translate-y-0.5"
            >
              How FCNR Works
            </Button>
          </div>

          {/* Trust chips */}
          <div className="flex flex-wrap gap-2 pt-2">
            {TRUST_CHIPS.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 rounded-lg bg-white/70 border border-[hsl(var(--zt-beige-deep))] px-3 py-1.5"
              >
                <Icon size={13} className="text-[hsl(var(--zt-sage))]" />
                <span className="font-heading text-[11.5px] font-medium text-[hsl(var(--zt-charcoal))]/85">{label}</span>
              </span>
            ))}
          </div>

          {/* Metrics row */}
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-5 pt-8 mt-2 border-t border-[hsl(var(--zt-beige-deep))]">
            {STATS.map((s) => (
              <div key={s.label}>
                <dd className="font-numeric text-[1.6rem] text-[hsl(var(--zt-teal))] font-semibold">{s.value}</dd>
                <dt className="font-heading text-[11px] text-[hsl(var(--zt-muted))] font-medium mt-1">{s.label}</dt>
              </div>
            ))}
          </dl>
        </motion.div>

        {/* ── Right: product illustration ────────────────────────── */}
        <motion.div
          className="lg:col-span-6 relative lg:-mr-8 xl:-mr-16"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          <HeroIllustration />
        </motion.div>
      </div>

      {/* ── Compliance strip ─────────────────────────────────────── */}
      <motion.div
        className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 mt-16 lg:mt-20 relative z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.35 }}
      >
        <div className="rounded-2xl bg-white border border-[hsl(var(--zt-beige-deep))] px-6 py-5 elev-1 flex flex-wrap items-center gap-x-10 gap-y-4 justify-between">
          <span className="font-heading text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--zt-muted))] font-bold">
            Regulated &amp; secure
          </span>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {[
              { icon: ShieldCheck, text: "SEBI-registered advisory" },
              { icon: Landmark, text: "RBI / FEMA compliant" },
              { icon: Globe2, text: "DTAA across 32 markets" },
              { icon: Lock, text: "Bank-grade encryption" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <Icon size={15} className="text-[hsl(var(--zt-gold))]" />
                <span className="font-heading text-[12.5px] font-semibold text-[hsl(var(--zt-charcoal))]/85">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
